import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError, brl, downloadCSV, rows, pageMeta } from "@/lib/api";
import { Wallet, TrendingUp, Percent, Download, Receipt, X } from "lucide-react";
import usePaginatedList from "@/hooks/usePaginatedList";
import Pagination from "@/components/panel/Pagination";
import { KpiCard, KpiSkeleton, EmptyState, ErrorState, TableSkeleton } from "@/components/panel/ui";
import { Badge, Button, Card, DataTable, FilterSheet, PageHeader, Select } from "@/components/ui-kit";
import Accounts from "./finance/Accounts";

const PERIODS = [["7", "7 dias"], ["30", "30 dias"], ["90", "90 dias"], ["", "Tudo"]];
const EXPORT_PAGE = 100;
const EXPORT_MAX_PAGES = 20; // teto de 2.000 linhas por exportação

/* Canal não é gravidade, é procedência: os dois tons só separam de onde veio a
   entrada (marca = loja, roxo = balcão), mesma leitura usada no resto do painel.
   Substitui as classes cruas bg-blue-50/bg-violet-50 que estavam na linha. */
const CHANNEL_TONE = { online: "info", balcao: "purple" };
/* O selo mostra rótulo, não o valor cru do banco: a linha exibia "balcao" sem
   acento enquanto o filtro logo acima dizia "Balcão". Mesmo par tom/rótulo de
   Sellers.js. O CSV segue exportando o valor cru, que é o que o Excel consome. */
const CHANNEL_LABEL = { online: "Online", balcao: "Balcão" };

export default function Finance() {
  const [aba, setAba] = useState("recebimentos");
  const [period, setPeriod] = useState("30");
  const [channel, setChannel] = useState("");
  const [exporting, setExporting] = useState(false);

  // Período e canal agora recortam no banco — os KPIs somam a base inteira
  // daquela fatia, e não só o que coube na tela.
  const from = period
    ? new Date(Date.now() - parseInt(period, 10) * 86400000).toISOString().slice(0, 10)
    : undefined;
  const filters = { channel: channel || undefined, from };

  const { data, isLoading: loading, isError: error, refetch: retry } = useQuery({
    queryKey: ["/admin/finance", filters],
    queryFn: async () => (await api.get("/admin/finance", { params: filters })).data,
  });

  const list = usePaginatedList("/admin/finance/receivables", filters);
  const receivables = list.items;

  // "30 dias" e "ambos canais" são os padrões da tela: não contam como filtro.
  const hasFilters = period !== "30" || !!channel;
  const clearFilters = () => { setPeriod("30"); setChannel(""); };
  const filtrosAtivos = (period !== "30" ? 1 : 0) + (channel ? 1 : 0);

  // O CSV não pode exportar só a página visível: percorre as páginas até o fim.
  const exportCSV = async () => {
    setExporting(true);
    try {
      const all = [];
      for (let page = 1; page <= EXPORT_MAX_PAGES; page += 1) {
        const res = await api.get("/admin/finance/receivables", {
          params: { ...filters, page, per_page: EXPORT_PAGE },
        });
        all.push(...rows(res));
        if (!pageMeta(res)?.has_more) break;
        if (page === EXPORT_MAX_PAGES) toast.warning(`Exportação limitada às ${all.length} linhas mais recentes.`);
      }
      downloadCSV(
        `contas-a-receber-${new Date().toISOString().slice(0, 10)}.csv`,
        ["Referência", "Canal", "Cliente", "Método", "Valor", "Data"],
        all.map((r) => [r.number, r.channel, r.customer, r.method,
          r.gross.toFixed(2).replace(".", ","), new Date(r.date).toLocaleString("pt-BR")]),
      );
    } catch (e) {
      // Sem isto o botão ficava travado em "Exportando" depois de uma página falhar.
      toast.error(apiError(e));
    } finally { setExporting(false); }
  };

  // Mesmos controles para a linha (desktop) e para a folha (mobile).
  const filtros = (
    <>
      {/* Segmentado, não Select: são quatro janelas fixas e a escolhida precisa
          ficar visível sem abrir nada. `aria-pressed` é o que diz qual está ativa. */}
      <div className="flex bg-white border border-slate-200 rounded-xl p-1" role="group" aria-label="Filtrar por período">
        {PERIODS.map(([v, l]) => (
          <button key={v} type="button" onClick={() => setPeriod(v)} data-testid={`fin-period-${v || "all"}`}
            aria-pressed={period === v}
            className={`text-xs font-semibold px-3 h-8 rounded-md transition-colors ${period === v ? "bg-brand-600 text-white" : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"}`}>
            {l}
          </button>
        ))}
      </div>
      <Select value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="fin-channel-filter"
        aria-label="Filtrar por canal" className="w-auto px-2.5 text-slate-600">
        <option value="">Ambos canais</option>
        <option value="online">Online</option>
        <option value="balcao">Balcão</option>
      </Select>
      {hasFilters && (
        <Button variant="link" size="sm" icon={X} onClick={clearFilters} data-testid="fin-clear-filters">Limpar filtros</Button>
      )}
    </>
  );

  if (error && aba === "recebimentos") return <ErrorState onRetry={retry} />;

  return (
    <div className="space-y-6" data-testid="admin-finance-page">
      <PageHeader
        eyebrow="Financeiro"
        title="Financeiro"
        icon={Wallet}
        description="O que já entrou pelos dois canais e o que está combinado para entrar ou sair. Contas a pagar e a receber ficam na segunda aba."
        actions={aba === "recebimentos" && (
          <Button variant="secondary" icon={Download} onClick={exportCSV} loading={exporting} data-testid="fin-export-btn">
            {exporting ? "Exportando..." : "CSV"}
          </Button>
        )}
        filters={aba === "recebimentos" ? filtros : null}
        mobileFilters={aba === "recebimentos"
          ? <FilterSheet count={filtrosAtivos} onClear={clearFilters}>{filtros}</FilterSheet>
          : null}
      />

      {/* Duas coisas diferentes na mesma tela: o que JÁ entrou (derivado de
          venda) e o que está COMBINADO (lançado à mão). Misturar as duas numa
          lista só faria o total dizer que a loja tem dinheiro que ainda não
          recebeu. */}
      <div className="flex bg-white border border-slate-200 rounded-xl p-1 w-fit" role="group" aria-label="Seção do financeiro">
        {[["recebimentos", "Recebimentos"], ["contas", "Contas a pagar e receber"]].map(([id, rotulo]) => (
          <button key={id} type="button" onClick={() => setAba(id)} aria-pressed={aba === id}
            data-testid={`fin-tab-${id}`}
            className={`text-xs font-semibold px-3 h-8 rounded-md transition-colors ${
              aba === id ? "bg-brand-600 text-white" : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"}`}>
            {rotulo}
          </button>
        ))}
      </div>

      {aba === "contas" && <Accounts />}

      {aba === "recebimentos" && (
        <>
      {loading || !data ? <KpiSkeleton count={3} /> : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <KpiCard icon={TrendingUp} label="Bruto no período" value={brl(data.gross)} testId="fin-kpi-gross" />
          <KpiCard icon={Wallet} label="Recebimentos" value={list.total} testId="fin-kpi-count" />
          <KpiCard icon={Percent} label="Comissões a pagar" value={brl(data.commissions_total)} cls="text-amber-600" testId="fin-kpi-commissions" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <section className="lg:col-span-2 space-y-3">
          {/* O título saiu de dentro do card: quem desenha a superfície agora é o
              DataTable, e um card dentro do outro empilharia duas bordas. */}
          <h2 className="font-heading text-sm font-semibold text-slate-900">Contas a receber</h2>
          <DataTable
        rowTestId="fin-receivable-row"
            caption="Contas a receber no período selecionado"
            rows={receivables}
            rowKey={(r) => r.id}
            loading={list.isLoading}
            error={list.isError}
            onRetry={list.refetch}
            /* A saída do vazio é sempre a que AMPLIA o recorte. "Limpar filtros"
               volta para 30 dias: em 90 dias vazios ele levaria a uma janela
               menor, garantidamente vazia também. Então a ordem é ampliar a
               janela primeiro, soltar o canal depois — e, sem janela nem canal,
               não há filtro a relaxar: recebível não nasce nesta tela. */
            empty={{
              icon: Receipt,
              title: "Nada a receber no período selecionado",
              desc: period
                ? `Nenhuma entrada nos últimos ${period} dias${channel ? ` pelo canal ${CHANNEL_LABEL[channel]}` : ""}. Amplie a janela para ver o que veio antes.`
                : channel
                  ? `Nenhuma entrada pelo canal ${CHANNEL_LABEL[channel]} em todo o histórico.`
                  : "Nenhum recebimento registrado. Vendas pagas da loja e do balcão aparecem aqui.",
              action: period
                ? <Button variant="secondary" onClick={() => setPeriod("")} data-testid="fin-empty-all-period">Ver todo o período</Button>
                : channel
                  ? <Button variant="secondary" onClick={clearFilters} data-testid="fin-empty-clear">Limpar filtros</Button>
                  : null,
              testId: "fin-receivables-empty",
            }}
            columns={[
              {
                key: "ref", header: "Ref", mono: true,
                cell: (r) => r.number,
              },
              {
                key: "canal", header: "Canal",
                cell: (r) => <Badge tone={CHANNEL_TONE[r.channel] || "neutral"}>{CHANNEL_LABEL[r.channel] || r.channel}</Badge>,
              },
              { key: "metodo", header: "Método", className: "text-slate-600 capitalize", cell: (r) => r.method.replace("_", " ") },
              { key: "valor", header: "Valor", align: "right", mono: true, className: "font-semibold", cell: (r) => brl(r.gross) },
            ]}
          />
          <Pagination page={list.page} lastPage={list.lastPage} total={list.total} onChange={list.setPage} label="recebimentos" />
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-sm font-semibold text-slate-900">Comissões a pagar</h2>
          {/* Resumo por vendedor, não listagem: sem cabeçalho de coluna, sem
              paginação e sem ação de linha. Fica em tabela simples dentro do
              card — o DataTable traria a própria superfície e os próprios
              estados para três linhas de agregado. */}
          <Card>
            {loading || !data ? <TableSkeleton rows={3} cols={3} />
              : data.commissions_payable.length === 0 ? (
                /* Era um `<td>` cinza solto — uma célula só numa tabela de três
                   colunas, sem colSpan. Mesmo EmptyState dos resumos embutidos
                   de Reports.js; aqui sem ação, porque comissão nasce da venda e
                   não há filtro a relaxar nesta coluna. */
                <EmptyState icon={Percent} title="Sem comissões a pagar"
                  desc="Nenhuma venda com comissão no recorte de período e canal."
                  testId="fin-commissions-empty" />
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {data.commissions_payable.map((c, i) => (
                      <tr key={i} className="border-b border-slate-100 last:border-0" data-testid="fin-commission-row">
                        <td className="px-5 py-2.5 text-slate-900">{c.seller}</td>
                        <td className="px-3 py-2.5 text-slate-500">{c.sales} vendas</td>
                        <td className="px-5 py-2.5 text-right font-mono tabular-nums font-semibold text-amber-600">{brl(c.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </Card>
        </section>
      </div>
        </>
      )}
    </div>
  );
}
